import { useState } from "react";
import ChartTooltip from "./ChartTooltip.jsx";
import HexRadar from "./HexRadar.jsx";
import BodyMap from "./BodyMap.jsx";
import BiggestMovers from "./BiggestMovers.jsx";
import { useElementWidth } from "../hooks/useElementWidth.js";
import { MUSCLE_BUCKETS } from "../utils/analytics.js";
import { deltaSign } from "../utils/format.js";
import styles from "./MuscleFocus.module.css";

const HEIGHT = 46;
const DOT_R = 2.2;
// Keeps a dot on the peak or on the baseline from being clipped at the edges.
const PAD = DOT_R + 2;
// Used until the ResizeObserver reports the real sparkline width.
const FALLBACK_WIDTH = 320;

function Sparkline({ label, values, weeks, average, width }) {
  const [hovered, setHovered] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const plotHeight = HEIGHT - PAD * 2;
  // Scaled against this muscle's own busiest week: the numbers beside the row
  // carry the size, so the line is free to spend its height on the shape.
  const peak = Math.max(...values, 1);
  const step = values.length > 1 ? (width - PAD * 2) / (values.length - 1) : 0;
  const x = (index) => PAD + index * step;
  const y = (value) => PAD + plotHeight - (value / peak) * plotHeight;
  const points = values.map((value, index) => `${x(index)},${y(value)}`).join(" ");
  // Each week owns the strip halfway to its neighbours, so the whole row is
  // live — hit areas pinned to the dots left the gaps between them dead.
  const bandLeft = (index) => (index === 0 ? 0 : (x(index - 1) + x(index)) / 2);
  const bandRight = (index) => (index === values.length - 1 ? width : (x(index) + x(index + 1)) / 2);
  // A muscle that was never trained is a flat line on the baseline; drawn in the
  // series colour it would read as steady low volume rather than as nothing.
  const untrained = average === 0;

  const enter = (index, event) => {
    setHovered(index);
    setPos({ x: event.clientX, y: event.clientY });
  };

  return (
    <>
      <svg
        className={styles.spark}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        role="img"
        aria-label={`${label}: ${average.toFixed(1)} sets per week on average across ${values.length} weeks.`}
      >
        {/* The row's own average across the period. */}
        {!untrained && (
          <line className={styles.average} x1={0} x2={width} y1={y(average)} y2={y(average)} />
        )}
        {hovered !== null && (
          <line className={styles.guide} x1={x(hovered)} x2={x(hovered)} y1={0} y2={HEIGHT} />
        )}
        <polyline className={untrained ? styles.lineZero : styles.line} points={points} />
        {values.map((value, index) => (
          <circle
            key={weeks[index].date}
            className={value ? styles.dot : styles.dotZero}
            cx={x(index)}
            cy={y(value)}
            r={index === hovered ? DOT_R * 2 : DOT_R}
          />
        ))}
        {/* Full-height bands, drawn last so they sit on top of the line. */}
        {values.map((_, index) => (
          <rect
            key={`${weeks[index].date}-hit`}
            className={styles.hit}
            x={bandLeft(index)}
            y={0}
            width={Math.max(bandRight(index) - bandLeft(index), 0)}
            height={HEIGHT}
            onMouseEnter={(event) => enter(index, event)}
            onMouseMove={(event) => setPos({ x: event.clientX, y: event.clientY })}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
      </svg>

      {hovered !== null && (
        <ChartTooltip className={styles.tooltip} x={pos.x} y={pos.y}>
          <span className={styles.tooltipLabel}>{label}</span>
          <span className={styles.tooltipValue}>
            Week of {weeks[hovered].label} — {values[hovered]} {values[hovered] === 1 ? "set" : "sets"}
          </span>
          {!untrained && (
            <span className={styles.tooltipAvg}>
              {vsAverage(values[hovered], average)}
            </span>
          )}
        </ChartTooltip>
      )}
    </>
  );
}

// Reads against the dashed line, which is the only reference the row has.
function vsAverage(value, average) {
  const delta = value - average;
  if (Math.abs(delta) < 0.05) return "on the average";
  return `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)} vs average`;
}

// The radar, the body map and these rows all read the same training out of one
// period: shape, anatomy, and week-by-week. They share a heading so the six
// buckets are one section rather than three panels that repeat each other.
export default function MuscleFocus({ buckets, prevBuckets, muscleCounts, prevMuscleCounts, weeklySeries, periodDays }) {
  // The sparklines are drawn in real pixels rather than stretched to fit, so
  // the dots stay round. Every row is the same width, so one measurement — the
  // first row's — drives all six.
  const [rowRef, rowWidth] = useElementWidth();

  const weeks = (weeklySeries || []).filter((w) => w.muscleSets);
  const hasWeeks = weeks.length >= 2;

  const current = buckets?.length ? buckets : MUSCLE_BUCKETS.map((b) => ({ ...b, setCount: 0 }));
  const previous = new Map((prevBuckets || []).map((b) => [b.id, b.setCount]));
  const hasPrevious = (prevBuckets || []).some((b) => b.setCount > 0);
  const total = current.reduce((sum, b) => sum + b.setCount, 0);

  const rows = [...current]
    .sort((a, b) => b.setCount - a.setCount)
    .map((bucket) => {
      const values = weeks.map((week) => week.muscleSets[bucket.id] || 0);
      const delta = bucket.setCount - (previous.get(bucket.id) || 0);
      return {
        ...bucket,
        values,
        delta,
        direction: delta > 0 ? "up" : delta < 0 ? "down" : "flat",
        share: total > 0 ? Math.round((bucket.setCount / total) * 100) : 0,
        perWeek: weeks.length ? values.reduce((sum, value) => sum + value, 0) / weeks.length : 0
      };
    });

  return (
    <section id="muscle-focus" className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Muscle focus</span>
        <span className={styles.total}>
          {total} sets
          {hasWeeks && (
            <span className={styles.headingExtra}>
              {" · "}{weeks.length} weeks · <span className={styles.legendDash}>dashed</span> average
            </span>
          )}
        </span>
      </div>

      <div className={styles.views}>
        <div className={styles.shape}>
          <HexRadar key={periodDays} buckets={buckets} prevBuckets={prevBuckets} />
          <BiggestMovers muscleCounts={muscleCounts} prevMuscleCounts={prevMuscleCounts} />
        </div>
        <BodyMap muscleCounts={muscleCounts} prevMuscleCounts={prevMuscleCounts} />
      </div>

      <div className={`${styles.rows} ${hasWeeks ? "" : styles.rowsNoSpark}`}>
        {rows.map((row, index) => (
          <div key={row.id} className={styles.row}>
            <span className={styles.name}>{row.label}</span>
            {hasWeeks && (
              <div className={styles.sparkCell} ref={index === 0 ? rowRef : undefined}>
                <Sparkline
                  label={row.label}
                  values={row.values}
                  weeks={weeks}
                  average={row.perWeek}
                  width={rowWidth || FALLBACK_WIDTH}
                />
              </div>
            )}
            {hasWeeks && (
              <span className={styles.rate}>{row.perWeek.toFixed(1)}<em>/wk</em></span>
            )}
            <span className={styles.stat}>
              {row.setCount > 0 ? `${row.setCount} sets · ${row.share}%` : "—"}
            </span>
            <span className={`${styles.delta} ${styles[row.direction]}`}>
              {hasPrevious && (
                <>
                  {deltaSign(row.direction)}
                  {row.delta !== 0 ? ` ${row.delta > 0 ? "+" : ""}${row.delta}` : ""}
                </>
              )}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
